// Purpose: View Promotion originals or sized copies only after explicit opening.
// Features: One full-resolution image at a time, lazy navigation, loading/retry,
// keyboard controls and accessible modal focus/dismissal.
// Dependencies: React, React DOM portals and existing Bootstrap classes.
// Important behavior: Neighbors are never mounted/preloaded; the body portal
// stays above manager navigation and restores focus/scrolling when unmounted.
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const ImagePreview = ({ image, index, sized }) => {
  const [status, setStatus] = useState("loading");
  const [attempt, setAttempt] = useState(0);
  const label = `${sized ? "Sized image" : "Image"} ${index + 1}`;
  const hasUrl = typeof image?.url === "string" && image.url.trim().length > 0;
  const hasDimensions = Number.isFinite(image?.width) && Number.isFinite(image?.height);

  return <figure className="mb-0 text-center">
    <figcaption className="fw-semibold mb-2">
      {label}{hasDimensions ? ` — ${image.width} × ${image.height} px` : ""}
    </figcaption>
    {!hasUrl || status === "error"
      ? <div className="alert alert-danger" role="alert">
        {label} could not be loaded.
        {hasUrl && <button type="button" className="btn btn-link" onClick={() => { setStatus("loading"); setAttempt((value) => value + 1); }}>Retry image</button>}
      </div>
      : <>
        {status === "loading" && <p className="text-muted" role="status">Loading {label.toLowerCase()}...</p>}
        <img key={attempt} src={image.url} alt={label}
          onLoad={() => setStatus("loaded")} onError={() => setStatus("error")}
          className="rounded border" aria-busy={status === "loading"}
          style={{ maxWidth: "100%", maxHeight: "70vh", width: "auto", height: "auto", margin: "auto", objectFit: "contain", display: status === "loading" ? "none" : "block" }} />
      </>}
  </figure>;
};

export default function PromotionSizedImagesModal({ post, images: suppliedImages, initialIndex = 0, title = "Sized Images", onClose }) {
  const images = Array.isArray(suppliedImages) ? suppliedImages : Array.isArray(post?.sizedImages) ? post.sizedImages : [];
  const [index, setIndex] = useState(() => Number.isInteger(initialIndex) && initialIndex >= 0 && initialIndex < images.length ? initialIndex : 0);
  const activeIndex = index < images.length ? index : 0;
  const dialogRef = useRef(null);
  const closeRef = useRef(onClose);
  const countRef = useRef(images.length);
  closeRef.current = onClose;
  countRef.current = images.length;
  const navigate = (direction) => {
    const count = countRef.current;
    if (count > 1) setIndex((value) => ((value < count ? value : 0) + direction + count) % count);
  };

  useEffect(() => {
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const controls = () => [...(dialogRef.current?.querySelectorAll("button:not(:disabled), a[href], [tabindex='0']") || [])];
    controls()[0]?.focus();
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault(); closeRef.current();
      }
      const input = event.target?.closest?.("input, textarea, select, [contenteditable='true']");
      if (!input && ["ArrowLeft", "ArrowRight"].includes(event.key) && countRef.current > 1) {
        event.preventDefault();
        const direction = event.key === "ArrowRight" ? 1 : -1;
        const count = countRef.current;
        setIndex((value) => ((value < count ? value : 0) + direction + count) % count);
      }
      if (event.key !== "Tab") return;
      const available = controls();
      const first = available[0];
      const last = available[available.length - 1];
      if (!available.length) { event.preventDefault(); dialogRef.current?.focus(); }
      else if (event.shiftKey && (document.activeElement === first || !dialogRef.current?.contains(document.activeElement))) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !dialogRef.current?.contains(document.activeElement))) {
        event.preventDefault(); first.focus();
      }
    };
    const keepFocus = (event) => {
      if (!dialogRef.current?.contains(event.target)) (controls()[0] || dialogRef.current)?.focus();
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("focusin", keepFocus);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("focusin", keepFocus);
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, []);

  const sized = title === "Sized Images";
  return createPortal(<div className="modal d-block" data-testid="sized-images-backdrop" style={{ backgroundColor: "rgba(0,0,0,0.65)", zIndex: 2200 }}
    onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
      <div className="modal-content" ref={dialogRef} role="dialog" aria-modal="true"
        aria-labelledby="promotion-sized-images-title" aria-describedby="promotion-sized-images-post" tabIndex={-1}>
        <div className="modal-header">
          <h2 className="modal-title h5" id="promotion-sized-images-title">{title}</h2>
          <button type="button" className="btn-close" aria-label={`Close ${title}`} onClick={onClose} />
        </div>
        <div className="modal-body">
          <p className="fw-semibold" id="promotion-sized-images-post">{post?.title || "Promotion"}</p>
          {!images.length ? <p className="text-muted">{sized ? "No sized images have been saved for this promotion." : "No images are available for this promotion."}</p>
            : <ImagePreview key={`${activeIndex}:${images[activeIndex]?.url || "missing"}`} image={images[activeIndex]} index={activeIndex} sized={sized} />}
        </div>
        <div className="modal-footer justify-content-between">
          {images.length > 1 && <div className="d-flex align-items-center gap-3">
            <button type="button" className="btn btn-outline-secondary" onClick={() => navigate(-1)}>Previous image</button>
            <span aria-live="polite" aria-atomic="true">{activeIndex + 1} of {images.length}</span>
            <button type="button" className="btn btn-outline-secondary" onClick={() => navigate(1)}>Next image</button>
          </div>}
          <button type="button" className="btn btn-secondary ms-auto" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  </div>, document.body);
}
